import type { ListViewDiff, TemporalStatus } from './types';

/* How one booking is marked in either view mode. A booking is marked against
 * the previous fetch (see ListViewDiff) and against the clock: one deleted in
 * Yesplan since the previous fetch is still rendered, struck through on a red
 * tint; a new one gets a green tint; changed fields an orange tint; and
 * independently of the diff, a booking that has ended is greyed out (still
 * readable) while one that is running right now carries a pulsing dot in
 * front of its name. The tints themselves live in ListViewBookings.style.
 *
 * The dot is what marks "right now" rather than a tint of its own: the
 * background colour of a row is what the diff says about it, and a fourth
 * tint there would be competing with those three for the same surface. */

export type RowMarker = ListViewDiff['statusByID'][string] | undefined;

export type RowDisplayStatus = 'deleted' | 'new' | 'changed' | 'past' | null;

/* One display status per row; a diff marker wins over the clock-derived
   status ('changed' tints only the changed cells, not the whole row) */
export function rowDisplayStatus(
  marker: RowMarker,
  temporal: TemporalStatus
): RowDisplayStatus {
  if (marker) return marker.status;
  if (temporal === 'past') return 'past';
  return null;
}

/* Whether the row is marked as running right now. Not part of the display
   status: the dot and the tints say different things, so a booking that is
   both new and running carries both marks. A deleted one is the exception -
   it is not booked at all any more, whatever its period says. */
export function isHappeningNow(marker: RowMarker, temporal: TemporalStatus): boolean {
  return temporal === 'active' && marker?.status !== 'deleted';
}
